import { useState, useEffect } from 'react'
import { useUIStore } from '../../stores/uiStore'
import * as authApi from '../../api/auth'
import Button from '../common/Button'
import Input from '../common/Input'

/**
 * User Create/Edit Modal
 * Allows admins to create new users or edit existing ones
 */
export default function UserModal({ user, onClose }) {
  const { showToast } = useUIStore()
  const isEditing = !!user

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'staff',
    trainer_id: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role || 'staff',
        trainer_id: user.trainer_id || ''
      })
    }
  }, [user])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.email) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format'
    }

    if (!formData.first_name) {
      newErrors.first_name = 'First name is required'
    }

    if (!formData.last_name) {
      newErrors.last_name = 'Last name is required'
    }

    if (!formData.role) {
      newErrors.role = 'Role is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setIsLoading(true)

    try {
      const userData = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        trainer_id: formData.trainer_id || null
      }

      if (isEditing) {
        await authApi.updateUser(user.id, userData)
        showToast('User updated successfully', 'success')
      } else {
        await authApi.createUser(userData)
        showToast('User created successfully. Welcome email sent.', 'success')
      }

      onClose(true) // true = refresh list
    } catch (error) {
      showToast(error.message || `Failed to ${isEditing ? 'update' : 'create'} user`, 'error')
      console.error('Error saving user:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-xl font-semibold text-gray-900">
            {isEditing ? 'Edit User' : 'Create New User'}
          </h3>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-500"
            disabled={isLoading}
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!isEditing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> A secure temporary password will be automatically generated and emailed to the user. They will be required to change it on first login.
              </p>
            </div>
          )}

          <Input
            label="Email"
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
            disabled={isLoading}
            placeholder="user@example.com"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name"
              name="first_name"
              required
              value={formData.first_name}
              onChange={handleChange}
              error={errors.first_name}
              disabled={isLoading}
              placeholder="John"
            />

            <Input
              label="Last Name"
              name="last_name"
              required
              value={formData.last_name}
              onChange={handleChange}
              error={errors.last_name}
              disabled={isLoading}
              placeholder="Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              disabled={isLoading}
              required
            >
              <option value="staff">Staff</option>
              <option value="admin">Administrator</option>
              <option value="trainer">Trainer</option>
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {formData.role === 'admin' && 'Full access to all features and settings'}
              {formData.role === 'staff' && 'Access to manage contracts, trainers, and teams'}
              {formData.role === 'trainer' && 'Limited access to assigned contracts only'}
            </p>
          </div>

          {formData.role === 'trainer' && (
            <Input
              label="Trainer ID (Optional)"
              name="trainer_id"
              type="number"
              value={formData.trainer_id}
              onChange={handleChange}
              disabled={isLoading}
              placeholder="Link to trainer record"
            />
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="secondary"
              onClick={() => onClose(false)}
              className="flex-1"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isLoading}
              loading={isLoading}
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update User' : 'Create User')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}